import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["status", "toggle"]
  static values = {
    blockedText: String,
    disabledText: String,
    disablingText: String,
    enabledText: String,
    enablingText: String,
    errorText: String,
    notificationsEnabled: Boolean,
    publicKeyUrl: String,
    subscriptionUrl: String,
    unavailableText: String
  }

  connect() {
    this.preferenceEnabled = this.notificationsEnabledValue
    this.setPreferenceState(this.preferenceEnabled)
    this.refreshState()
  }

  async toggle() {
    if (this.toggleTarget.checked) {
      await this.enable()
    } else {
      await this.disable()
    }
  }

  async enable() {
    this.setBusy(this.enablingTextValue)

    try {
      await this.savePreference(true)
      this.preferenceEnabled = true
      if (!this.supported) return this.setPreferenceState(true, this.unavailableTextValue)

      const permission = await Notification.requestPermission()
      if (permission !== "granted") return this.setPermissionState(permission)

      const registration = await navigator.serviceWorker.ready
      const subscription = await this.subscribe(registration)
      await this.saveSubscription(subscription)
      this.setPreferenceState(true)
    } catch (_error) {
      this.setError()
    }
  }

  async disable() {
    this.setBusy(this.disablingTextValue)

    try {
      const subscription = this.supported ? await this.currentSubscription() : null
      if (subscription) {
        await this.deleteSubscription(subscription.endpoint)
        await subscription.unsubscribe()
      } else {
        await this.deleteSubscription()
      }

      this.preferenceEnabled = false
      this.setPreferenceState(false)
    } catch (_error) {
      this.setError()
    }
  }

  async refreshState() {
    if (!this.supported) return this.setPreferenceState(this.preferenceEnabled, this.unavailableTextValue)
    if (Notification.permission === "denied") return this.setPermissionState(Notification.permission)

    try {
      await this.currentSubscription()
      this.setPreferenceState(this.preferenceEnabled)
    } catch (_error) {
      this.setError()
    }
  }

  async subscribe(registration) {
    const existingSubscription = await registration.pushManager.getSubscription()
    if (existingSubscription) return existingSubscription

    const publicKey = await this.fetchPublicKey()
    return registration.pushManager.subscribe({
      applicationServerKey: this.decodeBase64Url(publicKey),
      userVisibleOnly: true
    })
  }

  async fetchPublicKey() {
    const response = await fetch(this.publicKeyUrlValue, {
      credentials: "same-origin",
      headers: { Accept: "application/json" }
    })
    if (!response.ok) throw new Error("VAPID public key is unavailable")

    const payload = await response.json()
    return payload.public_key
  }

  async saveSubscription(subscription) {
    const payload = subscription.toJSON()
    const response = await fetch(this.subscriptionUrlValue, {
      body: JSON.stringify({
        push_subscription: {
          endpoint: subscription.endpoint,
          expiration_time: subscription.expirationTime,
          keys: payload.keys || {}
        }
      }),
      credentials: "same-origin",
      headers: this.jsonHeaders,
      method: "POST"
    })
    if (!response.ok) throw new Error("Push subscription was not saved")
  }

  async savePreference(enabled) {
    const response = await fetch(this.subscriptionUrlValue, {
      body: JSON.stringify({ notifications_enabled: enabled }),
      credentials: "same-origin",
      headers: this.jsonHeaders,
      method: "PATCH"
    })
    if (!response.ok) throw new Error("Notification preference was not saved")
  }

  async deleteSubscription(endpoint = null) {
    const response = await fetch(this.subscriptionUrlValue, {
      body: JSON.stringify(endpoint ? { endpoint } : {}),
      credentials: "same-origin",
      headers: this.jsonHeaders,
      method: "DELETE"
    })
    if (!response.ok) throw new Error("Push subscription was not deleted")
  }

  async currentSubscription() {
    const registration = await navigator.serviceWorker.ready
    return registration.pushManager.getSubscription()
  }

  decodeBase64Url(value) {
    const padding = "=".repeat((4 - (value.length % 4)) % 4)
    const base64 = `${value}${padding}`.replace(/-/g, "+").replace(/_/g, "/")
    const raw = window.atob(base64)
    const output = new Uint8Array(raw.length)

    for (let index = 0; index < raw.length; index += 1) {
      output[index] = raw.charCodeAt(index)
    }

    return output
  }

  setPreferenceState(enabled, text = null) {
    this.toggleTarget.checked = enabled
    this.toggleTarget.disabled = false
    this.statusTarget.textContent = text || (enabled ? this.enabledTextValue : this.disabledTextValue)
  }

  setPermissionState(permission) {
    this.toggleTarget.checked = this.preferenceEnabled
    this.toggleTarget.disabled = false
    this.statusTarget.textContent = permission === "denied" ? this.blockedTextValue : this.enabledTextValue
  }

  setUnavailable() {
    this.toggleTarget.checked = false
    this.toggleTarget.disabled = true
    this.statusTarget.textContent = this.unavailableTextValue
  }

  setBusy(text) {
    this.toggleTarget.disabled = true
    this.statusTarget.textContent = text
  }

  setError() {
    this.toggleTarget.disabled = false
    this.setPreferenceState(this.preferenceEnabled)
    this.statusTarget.textContent = this.errorTextValue
  }

  get jsonHeaders() {
    const headers = {
      Accept: "application/json",
      "Content-Type": "application/json"
    }
    const csrfToken = document.querySelector("meta[name='csrf-token']")?.content
    if (csrfToken) headers["X-CSRF-Token"] = csrfToken

    return headers
  }

  get supported() {
    return "serviceWorker" in navigator && "PushManager" in window && "Notification" in window
  }
}
